import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProblemFilters } from '@/components/problems/ProblemFilters';
import { EMPTY_FILTERS, type Filters } from '@/components/problems/filters';
import { ProblemTable } from '@/components/problems/ProblemTable';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { meApi, problemsApi } from '@/api/endpoints';
import { mapProblemSummary, mapSolveStatus } from '@/api/mappers';
import type { MeProgressResponse } from '@/api/types';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { useAuthStore } from '@/store/auth';
import type { Difficulty } from '@/types';
import { normalizeTopicParam, topicPath } from '@/data/topics';
import { difficultyPath } from '@/data/difficulties';
import { CollectionLinks } from '@/components/problems/ProblemCollection';
import { useInitialData } from '@/ssr/initialData';

const PAGE_SIZE = 12;

const API_DIFFICULTY: Record<Difficulty, 'EASY' | 'MEDIUM' | 'HARD'> = {
  easy: 'EASY',
  medium: 'MEDIUM',
  hard: 'HARD',
};

const DIFF_COLORS: Record<Difficulty, string> = {
  easy: 'var(--easy)',
  medium: 'var(--medium)',
  hard: 'var(--hard)',
};

export function Problems() {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();

  // Prerender: 1-sahifa (filtrsiz) HTML ichida keladi. Hydration server markup'i bilan
  // mos bo'lishi uchun ?topic= filtri birinchi renderdan keyin (effect'da) qo'llanadi.
  const initial = useInitialData();
  const initialList = initial?.problems ?? null;
  const pendingTopic = useRef(initialList ? searchParams.get('topic') : null);

  const [filters, setFilters] = useState<Filters>({
    ...EMPTY_FILTERS,
    topic: initialList ? 'all' : normalizeTopicParam(searchParams.get('topic')),
  });
  const [page, setPage] = useState(0);

  useEffect(() => {
    const topic = normalizeTopicParam(pendingTopic.current);
    pendingTopic.current = null;
    if (topic !== 'all') setFilters((f) => ({ ...f, topic }));
  }, []);

  // Qidiruvni debounce qilamiz — har harfda backendga so'rov yubormaslik uchun
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search.trim());
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => clearTimeout(id);
  }, [filters.search]);

  // Filtr/qidiruv natijasi — mavzu/qiyinlik sahifalarining dublikati: noindex, canonical toza
  // sahifaga (faqat mavzu → /topics/x, faqat qiyinlik → /problems/difficulty/x, aks holda /problems).
  // nginx ham ?topic=/?difficulty=/?search=/?sort=/?tag= uchun X-Robots-Tag: noindex qo'yadi.
  const onlyTopic = filters.topic !== 'all' && filters.difficulty === 'all' && !debouncedSearch;
  const onlyDifficulty = filters.difficulty !== 'all' && filters.topic === 'all' && !debouncedSearch;
  const filtered = filters.topic !== 'all' || filters.difficulty !== 'all' || Boolean(debouncedSearch);
  useSeo({
    title: 'SQL masalalar | SQLMaster.uz',
    description:
      "PostgreSQL SQL masalalari to'plami: JOIN, oyna funksiyalari, agregatsiya, subquery va boshqalar. Har bir masala real jadval sxemasi bilan.",
    path: onlyTopic
      ? topicPath(filters.topic)
      : onlyDifficulty
        ? difficultyPath(API_DIFFICULTY[filters.difficulty as Difficulty])
        : '/problems',
    noindex: filtered,
  });

  const [items, setItems] = useState<ReturnType<typeof mapProblemSummary>[]>(
    () => initialList?.content.map((p) => mapProblemSummary(p)) ?? [],
  );
  const [totalElements, setTotalElements] = useState(initialList?.totalElements ?? 0);
  const [totalPages, setTotalPages] = useState(Math.max(1, initialList?.totalPages ?? 1));
  const [loading, setLoading] = useState(!initialList);
  /** Prerender ro'yxati aynan shu so'rovniki bo'lsa — birinchi fetch kerak emas. */
  const skipLoadKey = useRef(initialList ? `0|all|all||${initial?.lang}` : null);
  const [error, setError] = useState(false);

  /** Kirgan foydalanuvchi uchun progress; kirmaganda null (barchasi 'todo'). */
  const [progress, setProgress] = useState<MeProgressResponse | null>(null);

  // Filtr o'zgarganda 1-sahifaga qaytish + URL sync (topic)
  useEffect(() => {
    setPage(0);
    const next = new URLSearchParams(searchParams);
    if (filters.topic !== 'all') next.set('topic', filters.topic);
    else next.delete('topic');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.difficulty, filters.topic, debouncedSearch]);

  // Progress alohida yuklanadi — masalalar ro'yxati public, progress esa shaxsiy.
  useEffect(() => {
    if (!isAuthenticated) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    meApi
      .progress()
      .then((res) => {
        if (!cancelled) setProgress(res.data);
      })
      .catch(() => {
        // Progress yuklanmasa ro'yxat baribir ko'rsatiladi — belgilar 'todo' bo'lib qoladi
        if (!cancelled) setProgress(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const loadProblems = useCallback(() => {
    const key = `${page}|${filters.difficulty}|${filters.topic}|${debouncedSearch}|${lang}`;
    const skip = skipLoadKey.current === key;
    skipLoadKey.current = null;
    if (skip) return;
    let cancelled = false;
    setLoading(true);
    setError(false);

    problemsApi
      .list({
        page,
        size: PAGE_SIZE,
        difficulty: filters.difficulty === 'all' ? undefined : API_DIFFICULTY[filters.difficulty],
        topic: filters.topic === 'all' ? undefined : filters.topic,
        search: debouncedSearch || undefined,
        lang,
      })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data.content.map((p) => mapProblemSummary(p)));
        setTotalElements(res.data.totalElements);
        setTotalPages(Math.max(1, res.data.totalPages));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, filters.difficulty, filters.topic, debouncedSearch, lang]);

  useEffect(loadProblems, [loadProblems]);

  // Progress ro'yxatdan keyin kelishi mumkin — belgilarni shu yerda birlashtiramiz
  const withStatus = useMemo(
    () => items.map((p) => ({ ...p, status: mapSolveStatus(progress?.statuses[String(p.id)]) })),
    [items, progress],
  );

  // Qidiruv/filtr endi backendда bajariladi — bu yerda faqat progress birlashtiriladi
  const visible = withStatus;

  const from = totalElements === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, totalElements);

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold sm:text-3xl">{t('problems.title')}</h1>
        <p className="mt-1 text-content-secondary">{t('problems.subtitle')}</p>
      </div>

      {/* Progress kartalari — faqat kirgan foydalanuvchiga */}
      {progress && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(['easy', 'medium', 'hard'] as Difficulty[]).map((d) => {
            const stat = progress.byDifficulty[API_DIFFICULTY[d]] ?? { solved: 0, total: 0 };
            return (
              <div key={d} className="card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: DIFF_COLORS[d] }}>
                    {t(`difficulty.${d}`)}
                  </span>
                  <span className="tnum text-sm text-content-secondary">
                    {stat.solved} / {stat.total}
                  </span>
                </div>
                <ProgressBar value={stat.solved} max={stat.total} color={DIFF_COLORS[d]} />
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="mb-5">
        <ProblemFilters filters={filters} onChange={setFilters} />
      </div>

      {/* Table */}
      {error ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('problems.loadError')}
        </div>
      ) : loading ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('common.loading')}
        </div>
      ) : (
        <ProblemTable problems={visible} />
      )}

      {/* Pagination */}
      {!loading && !error && totalElements > 0 && (
        <div className="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-sm text-content-muted">
            {t('problems.showing', { from, to, total: totalElements })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn btn-secondary px-3 py-2 text-sm"
            >
              {t('problems.prev')}
            </button>
            <span className="tnum px-2 text-sm text-content-secondary">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              className="btn btn-secondary px-3 py-2 text-sm"
            >
              {t('problems.next')}
            </button>
          </div>
        </div>
      )}
      <CollectionLinks />
    </div>
  );
}
